import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Calendar, Clock, Search } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import useSeo from "../hooks/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function News() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");

  useSeo({
    title: "Health Blog & Practice News | Beaudesert General Practice",
    description: "Seasonal flu-shot reminders, skin-check campaigns and plain-English health tips from the Beaudesert General Practice team.",
    path: "/news"
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/posts`)
      .then((r) => Array.isArray(r.data) && setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [posts]);

  const filtered = useMemo(() => {
    let list = posts;
    if (category !== "All") list = list.filter((p) => p.category === category);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((p) =>
        [p.title, p.excerpt, p.category].some((f) => (f || "").toLowerCase().includes(s))
      );
    }
    return list;
  }, [posts, q, category]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="relative bg-[#f0edfa] min-h-screen" style={{ overflowX: "clip" }}>
      <Navbar />

      {/* Header */}
      <section className="relative px-4 md:px-8 pt-10 pb-10 md:pt-14 molecule-bg">
        <div className="mx-auto max-w-[1400px]">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#1a2464] font-semibold text-sm mb-6 hover:text-[#3dc9b3]"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 items-end">
            <div>
              <span className="eyebrow">Health blog</span>
              <h1 className="section-title">News, health tips & practice updates</h1>
            </div>
            <p className="text-[#4a4a63] leading-relaxed lg:ml-auto lg:max-w-md">
              Seasonal flu-shot reminders, skin-check campaigns, and plain-English health tips from the Beaudesert General Practice team.
            </p>
          </div>

          {/* Search + categories */}
          <div className="mt-10 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-2 bg-white rounded-full border border-[#eee6f7] px-5 py-2 flex-1 max-w-md">
              <Search className="w-4 h-4 text-[#6b6b85]" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles..."
                className="bg-transparent flex-1 focus:outline-none text-sm text-[#1a1a2e] placeholder-[#6b6b85]/70"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`text-xs font-semibold px-4 py-2 rounded-full transition-colors ${
                    category === c
                      ? "bg-[#1a2464] text-white"
                      : "bg-white text-[#1a2464] border border-[#eee6f7] hover:bg-[#1a2464] hover:text-white"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* List */}
      <section className="px-4 md:px-8 pb-20">
        <div className="mx-auto max-w-[1400px]">
          {loading && <div className="h-64 rounded-3xl bg-white/60 animate-pulse" />}

          {!loading && filtered.length === 0 && (
            <div className="bg-white rounded-3xl p-10 text-center border border-[#eee6f7]">
              <div className="text-[#1a2464] font-bold text-lg">No articles match your search</div>
              <div className="text-[#4a4a63] text-sm mt-2">Try clearing the search or picking another category.</div>
            </div>
          )}

          {featured && (
            <Link
              to={`/news/${featured.slug}`}
              className="group block bg-white rounded-3xl overflow-hidden border border-[#eee6f7] shadow-sm hover:shadow-xl transition-all mb-8"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="aspect-[16/10] lg:aspect-auto lg:h-full overflow-hidden bg-[#eae6f6]">
                  <img
                    src={featured.cover}
                    alt={featured.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex bg-[#3dc9b3] text-white font-semibold text-[11px] px-3 py-1 rounded-full">
                      Featured
                    </span>
                    <span className="inline-flex bg-[#3dc9b3]/15 text-[#1a2464] font-semibold text-[11px] px-3 py-1 rounded-full">
                      {featured.category}
                    </span>
                  </div>
                  <h2 className="font-bold text-[#1a2464] text-2xl md:text-3xl leading-tight mb-4 group-hover:text-[#3dc9b3] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="text-[#4a4a63] leading-relaxed mb-6">{featured.excerpt}</p>
                  <div className="flex items-center gap-4 text-[#6b6b85] text-xs">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(featured.published_at)}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {featured.read_time}</span>
                    <span>By {featured.author}</span>
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 text-[#1a2464] font-semibold group-hover:text-[#3dc9b3]">
                    Read the article <ArrowUpRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </Link>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/news/${p.slug}`}
                className="group bg-white rounded-3xl overflow-hidden border border-[#eee6f7] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#eae6f6]">
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex bg-[#3dc9b3]/15 text-[#1a2464] font-semibold text-[11px] px-3 py-1 rounded-full">{p.category}</span>
                    <span className="text-[#6b6b85] text-xs inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {p.read_time}</span>
                  </div>
                  <h3 className="font-bold text-[#1a2464] text-lg leading-snug mb-3 group-hover:text-[#3dc9b3] transition-colors">{p.title}</h3>
                  <p className="text-[#4a4a63] text-sm leading-relaxed line-clamp-3">{p.excerpt}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <span className="text-[#6b6b85] text-xs inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(p.published_at)}</span>
                    <span className="text-[#1a2464] font-semibold text-sm inline-flex items-center gap-1 group-hover:text-[#3dc9b3]">Read <ArrowUpRight className="w-4 h-4" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
